import { PixelBuf } from './pixel';

/**
 * Original 5x7 bitmap font, baked into a per-colour atlas on first use.
 * Text is upper-case only (classic console style); lower case is folded up.
 */

export const GLYPH_W = 5;
export const GLYPH_H = 7;
export const ADVANCE = 6;

const G: Record<string, string[]> = {
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  B: ['####.', '#...#', '#...#', '####.', '#...#', '#...#', '####.'],
  C: ['.####', '#....', '#....', '#....', '#....', '#....', '.####'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  F: ['#####', '#....', '#....', '####.', '#....', '#....', '#....'],
  G: ['.###.', '#...#', '#....', '#..##', '#...#', '#...#', '.###.'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  I: ['.###.', '..#..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  J: ['..###', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  K: ['#...#', '#..#.', '#.#..', '##...', '#.#..', '#..#.', '#...#'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  M: ['#...#', '##.##', '#.#.#', '#...#', '#...#', '#...#', '#...#'],
  N: ['#...#', '##..#', '#.#.#', '#..##', '#...#', '#...#', '#...#'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  P: ['####.', '#...#', '#...#', '####.', '#....', '#....', '#....'],
  Q: ['.###.', '#...#', '#...#', '#...#', '#.#.#', '#..#.', '.##.#'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  T: ['#####', '..#..', '..#..', '..#..', '..#..', '..#..', '..#..'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  V: ['#...#', '#...#', '#...#', '#...#', '#...#', '.#.#.', '..#..'],
  W: ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '##.##', '#...#'],
  X: ['#...#', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '#...#'],
  Y: ['#...#', '#...#', '.#.#.', '..#..', '..#..', '..#..', '..#..'],
  Z: ['#####', '....#', '...#.', '..#..', '.#...', '#....', '#####'],
  '0': ['.###.', '#...#', '#..##', '#.#.#', '##..#', '#...#', '.###.'],
  '1': ['..#..', '.##..', '..#..', '..#..', '..#..', '..#..', '.###.'],
  '2': ['.###.', '#...#', '....#', '...#.', '..#..', '.#...', '#####'],
  '3': ['####.', '....#', '....#', '.###.', '....#', '....#', '####.'],
  '4': ['#..#.', '#..#.', '#..#.', '#####', '...#.', '...#.', '...#.'],
  '5': ['#####', '#....', '#....', '####.', '....#', '....#', '####.'],
  '6': ['..##.', '.#...', '#....', '####.', '#...#', '#...#', '.###.'],
  '7': ['#####', '....#', '...#.', '..#..', '.#...', '.#...', '.#...'],
  '8': ['.###.', '#...#', '#...#', '.###.', '#...#', '#...#', '.###.'],
  '9': ['.###.', '#...#', '#...#', '.####', '....#', '...#.', '.##..'],
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....'],
  '!': ['..#..', '..#..', '..#..', '..#..', '..#..', '.....', '..#..'],
  '?': ['.###.', '#...#', '....#', '..##.', '..#..', '.....', '..#..'],
  '.': ['.....', '.....', '.....', '.....', '.....', '.##..', '.##..'],
  ',': ['.....', '.....', '.....', '.....', '.##..', '.##..', '.#...'],
  ':': ['.....', '.##..', '.##..', '.....', '.##..', '.##..', '.....'],
  '-': ['.....', '.....', '.....', '#####', '.....', '.....', '.....'],
  '+': ['.....', '..#..', '..#..', '#####', '..#..', '..#..', '.....'],
  '=': ['.....', '.....', '#####', '.....', '#####', '.....', '.....'],
  '/': ['....#', '....#', '...#.', '..#..', '.#...', '#....', '#....'],
  X_MUL: ['.....', '#...#', '.#.#.', '..#..', '.#.#.', '#...#', '.....'],
  '(': ['..##.', '.#...', '.#...', '.#...', '.#...', '.#...', '..##.'],
  ')': ['.##..', '...#.', '...#.', '...#.', '...#.', '...#.', '.##..'],
  '<': ['...#.', '..#..', '.#...', '#....', '.#...', '..#..', '...#.'],
  '>': ['.#...', '..#..', '...#.', '....#', '...#.', '..#..', '.#...'],
  '%': ['#...#', '...#.', '..#..', '..#..', '.#...', '#...#', '.....'],
  "'": ['..#..', '..#..', '.....', '.....', '.....', '.....', '.....'],
  '*': ['.....', '#.#.#', '.###.', '#####', '.###.', '#.#.#', '.....'],
};

/** Order of glyphs inside the baked atlas. */
const ORDER = Object.keys(G);
const INDEX = new Map<string, number>(ORDER.map((k, i) => [k, i]));

export class Font {
  private atlases = new Map<string, HTMLCanvasElement>();

  private buildAtlas(color: string): HTMLCanvasElement {
    const buf = new PixelBuf(ORDER.length * GLYPH_W, GLYPH_H);
    ORDER.forEach((ch, i) => {
      const rows = G[ch];
      for (let y = 0; y < GLYPH_H; y++) {
        for (let x = 0; x < GLYPH_W; x++) {
          if (rows[y][x] === '#') buf.px(i * GLYPH_W + x, y, color);
        }
      }
    });
    return buf.canvas;
  }

  private atlas(color: string): HTMLCanvasElement {
    let a = this.atlases.get(color);
    if (!a) {
      a = this.buildAtlas(color);
      this.atlases.set(color, a);
    }
    return a;
  }

  static width(text: string): number {
    return text.length * ADVANCE - 1;
  }

  /** `x`/`y` are screen pixels; y is the top of the glyph box. */
  draw(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    color = '#ffffff',
    align: 'left' | 'center' | 'right' = 'left',
  ): void {
    const t = text.toUpperCase();
    let ox = Math.round(x);
    if (align === 'center') ox -= Math.round(Font.width(t) / 2);
    else if (align === 'right') ox -= Font.width(t);
    const atlas = this.atlas(color);
    const oy = Math.round(y);
    for (let i = 0; i < t.length; i++) {
      let ch = t[i];
      if (ch === '×') ch = 'X_MUL';
      const gi = INDEX.get(ch);
      if (gi === undefined) {
        ox += ADVANCE;
        continue;
      }
      if (ch !== ' ') {
        ctx.drawImage(atlas, gi * GLYPH_W, 0, GLYPH_W, GLYPH_H, ox, oy, GLYPH_W, GLYPH_H);
      }
      ox += ADVANCE;
    }
  }

  /** Text with a 1px drop shadow, used for the HUD. */
  drawShadowed(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    color: string,
    shadow: string,
    align: 'left' | 'center' | 'right' = 'left',
  ): void {
    this.draw(ctx, text, x + 1, y + 1, shadow, align);
    this.draw(ctx, text, x, y, color, align);
  }
}
